import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useApi } from '../../hooks/useApi';
import { useProductsContext } from '../../context/ProductsContext';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { capture } from '../../analytics/posthog';
import {
  Search,
  Building2,
  Users,
  Handshake,
  FileText,
  Briefcase,
  ShoppingCart,
  Calendar,
  FolderKanban,
  LayoutDashboard,
  Mail,
  CheckSquare,
  Trophy,
  Package,
  TrendingUp,
  UserCog,
  Newspaper,
  ArrowRight,
  Loader2,
} from 'lucide-react';

const ENTITY_CONFIG = {
  organizations: { label: 'Organizations', icon: Building2 },
  people: { label: 'People', icon: Users },
  deals: { label: 'Deals', icon: Handshake },
  quotes: { label: 'Quotes', icon: FileText },
  jobs: { label: 'Jobs', icon: Briefcase },
  purchase_orders: { label: 'Purchase Orders', icon: ShoppingCart },
  meetings: { label: 'Meetings', icon: Calendar },
  projects: { label: 'Projects', icon: FolderKanban },
};

// Display order
const ENTITY_ORDER = ['organizations', 'people', 'deals', 'quotes', 'jobs', 'purchase_orders', 'meetings', 'projects'];

// All page shortcuts with keywords for matching
const ALL_PAGE_SHORTCUTS = [
  { name: 'My Dashboard', route: '/', icon: LayoutDashboard, keywords: ['dashboard', 'home'] },
  { name: 'Email', route: '/emails', icon: Mail, keywords: ['email', 'mail', 'inbox'] },
  { name: 'Tasks', route: '/tasks', icon: CheckSquare, keywords: ['tasks', 'todo'] },
  { name: 'Projects', route: '/projects', icon: FolderKanban, keywords: ['projects'] },
  { name: 'Calendar', route: '/meetings', icon: Calendar, keywords: ['calendar', 'meetings', 'schedule'] },
  { name: 'Sales Manager', route: '/sales-manager', icon: TrendingUp, keywords: ['sales', 'dashboard', 'sales dashboard', 'sales manager', 'coaching', 'rep', 'team'], requiresSalesDashboard: true },
  { name: 'Leaderboard', route: '/leaderboard', icon: Trophy, keywords: ['leaderboard', 'ranking'], requiresSales: true, requiresLeaderboard: true },
  { name: 'Deals', route: '/deals', icon: Handshake, keywords: ['deals', 'pipeline'], requiresSales: true },
  { name: 'Quotes', route: '/quotes', icon: FileText, keywords: ['quotes', 'pricing'], requiresSales: true },
  { name: 'Jobs', route: '/jobs', icon: Briefcase, keywords: ['jobs', 'operations'], requiresOps: true },
  { name: 'Inventory', route: '/inventory', icon: Package, keywords: ['inventory', 'stock', 'warehouse'], requiresOps: true },
  { name: 'Companies', route: '/companies', icon: Building2, keywords: ['companies', 'customers', 'operators', 'organizations', 'rolodex'] },
  { name: 'People', route: '/people', icon: Users, keywords: ['contacts', 'people', 'rolodex'] },
  { name: 'Admin', route: '/admin/users', icon: UserCog, keywords: ['admin', 'users', 'settings', 'management'], requiresAdmin: true },
  { name: "What's New", route: '/updates', icon: Newspaper, keywords: ['updates', 'changelog', 'news', "what's new"] },
];

export default function UniversalSearch({ isOpen, onClose }) {
  useBodyScrollLock(isOpen);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const { apiCall } = useApi();
  const { isCalculator, permissions } = useProductsContext();

  // Filter page shortcuts by permissions
  const pageShortcuts = useMemo(() => {
    if (isCalculator) {
      const allowed = ['/tasks', '/emails', '/projects', '/meetings'];
      return ALL_PAGE_SHORTCUTS.filter(s => allowed.includes(s.route));
    }
    return ALL_PAGE_SHORTCUTS.filter(s => {
      if (s.requiresSales && !permissions.canAccessSales) return false;
      if (s.requiresSalesDashboard && !permissions.canViewSalesDashboard) return false;
      if (s.requiresOps && !permissions.canAccessOps) return false;
      if (s.requiresAdmin && !permissions.canAccessAdmin) return false;
      if (s.requiresInvestors && !permissions.canAccessInvestors) return false;
      if (s.requiresLeaderboard && !permissions.canViewSalesLeaderboard) return false;
      return true;
    });
  }, [isCalculator, permissions]);

  // Match page shortcuts against query
  const matchedPages = useMemo(() => {
    if (!query.trim()) return pageShortcuts;
    const term = query.toLowerCase().trim();
    return pageShortcuts.filter(s =>
      s.name.toLowerCase().includes(term) ||
      s.keywords.some(k => k.includes(term))
    );
  }, [query, pageShortcuts]);

  // Build flat list of all selectable items for keyboard nav
  const flatItems = useMemo(() => {
    const items = [];

    // Page shortcuts first (when query is empty or matches)
    if (matchedPages.length > 0) {
      for (const page of matchedPages) {
        items.push({ type: 'page', ...page });
      }
    }

    // API results grouped by entity type
    for (const key of ENTITY_ORDER) {
      if (results[key]) {
        for (const item of results[key]) {
          items.push({ type: key, ...item });
        }
      }
    }

    return items;
  }, [matchedPages, results]);

  // Reset state when opened/closed
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults({});
      setSelectedIndex(0);
      setLoading(false);
      // Focus input after portal renders
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOpen]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setResults({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall(`universal-search?q=${encodeURIComponent(searchQuery)}`);
      const buckets = data.results || {};
      setResults(buckets);
      const resultsCount = Object.values(buckets).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0,
      );
      // Send query length only — never the query string itself.
      capture('global_search_used', {
        query_length: trimmed.length,
        results_count: resultsCount,
      });
    } catch {
      setResults({});
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(0);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value), 300);
  }, [performSearch]);

  // Navigate to a result
  const handleSelect = useCallback((item) => {
    onClose();
    navigate(item.route);
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatItems.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[selectedIndex]) {
        handleSelect(flatItems[selectedIndex]);
      }
    }
  }, [flatItems, selectedIndex, handleSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  const hasApiResults = Object.keys(results).length > 0;
  const hasQuery = query.trim().length >= 2;
  const showEmpty = hasQuery && !loading && !hasApiResults && matchedPages.length === 0;

  // Track flat index across sections
  let flatIndex = -1;

  return createPortal(
    <div
      className="fixed inset-0 z-50"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Dialog */}
      <div className="relative flex justify-center pt-[15vh] px-4">
        <div
          className="w-full max-w-[640px] bg-white rounded-ae-sm shadow-xl overflow-hidden flex flex-col"
          style={{ maxHeight: '60vh' }}
          onClick={e => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b" style={{ borderColor: 'var(--border)' }}>
            {loading ? (
              <Loader2 size={20} className="text-gray-400 animate-spin flex-shrink-0" />
            ) : (
              <Search size={20} className="text-gray-400 flex-shrink-0" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder="Search for anything..."
              className="flex-1 h-12 bg-transparent text-sm outline-none placeholder:text-gray-400"
              style={{ color: 'var(--color-text-primary)' }}
            />
            <kbd
              className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded border flex-shrink-0"
              style={{ color: 'var(--color-text-tertiary)', borderColor: 'var(--border)', backgroundColor: 'var(--bg-sunken)' }}
            >
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="overflow-y-auto" role="listbox">
            {/* Page shortcuts */}
            {matchedPages.length > 0 && (
              <ResultSection label={hasQuery ? 'Pages' : 'Quick links'}>
                {matchedPages.map(page => {
                  flatIndex++;
                  const idx = flatIndex;
                  const Icon = page.icon;
                  return (
                    <ResultItem
                      key={page.route}
                      selected={selectedIndex === idx}
                      onClick={() => handleSelect(page)}
                    >
                      <Icon size={16} className="flex-shrink-0" style={{ color: 'var(--color-teal)' }} />
                      <span className="truncate text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {page.name}
                      </span>
                      <ArrowRight size={14} className="ml-auto flex-shrink-0 text-gray-300" />
                    </ResultItem>
                  );
                })}
              </ResultSection>
            )}

            {/* API results by entity type */}
            {ENTITY_ORDER.map(key => {
              const items = results[key];
              if (!items || items.length === 0) return null;
              const config = ENTITY_CONFIG[key];
              const SectionIcon = config.icon;

              return (
                <ResultSection key={key} label={config.label}>
                  {items.map(item => {
                    flatIndex++;
                    const idx = flatIndex;
                    return (
                      <ResultItem
                        key={item.id}
                        selected={selectedIndex === idx}
                        onClick={() => handleSelect(item)}
                      >
                        <SectionIcon size={16} className="flex-shrink-0" style={{ color: 'var(--color-teal)' }} />
                        <div className="flex-1 min-w-0">
                          <span className="block truncate text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {item.name}
                          </span>
                          {item.subtitle && (
                            <span className="block truncate text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                              {item.subtitle}
                            </span>
                          )}
                        </div>
                      </ResultItem>
                    );
                  })}
                </ResultSection>
              );
            })}

            {/* Empty state */}
            {showEmpty && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  No results found for &ldquo;{query}&rdquo;
                </p>
              </div>
            )}
          </div>

          {/* Footer with keyboard hints */}
          <div
            className="hidden sm:flex items-center gap-4 px-4 py-2 text-[11px] border-t"
            style={{ color: 'var(--color-text-tertiary)', borderColor: 'var(--border)' }}
          >
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-sunken)' }}>↑</kbd>
              <kbd className="inline-flex items-center justify-center w-5 h-5 rounded border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-sunken)' }}>↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center justify-center px-1.5 h-5 rounded border text-[10px]" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-sunken)' }}>↵</kbd>
              open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center justify-center px-1.5 h-5 rounded border text-[10px]" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-sunken)' }}>esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ResultSection({ label, children }) {
  return (
    <div className="py-1">
      <div className="px-4 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

function ResultItem({ children, selected, onClick }) {
  return (
    <div
      role="option"
      aria-selected={selected}
      data-selected={selected}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer transition-colors"
      style={{
        backgroundColor: selected ? 'var(--color-teal-light)' : undefined,
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-teal-light)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = ''; }}
    >
      {children}
    </div>
  );
}
